// Sharing Rules management page (Admin only)
import { useState } from 'react'
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/20/solid'
import {
  useSharingRules,
  useRoles,
  useEntityFields,
  useCreateSharingRule,
  useUpdateSharingRule,
  useDeleteSharingRule,
  useToggleSharingRule,
  type SharingRule,
  type SharingRuleInput,
} from '../../hooks/useSharingRules'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'

const emptyRule: Partial<SharingRuleInput> = {
  name: '',
  description: '',
  entity: 'leads',
  ruleType: 'owner_based',
  shareFromRole: '',
  criteriaField: '',
  criteriaOperator: '=',
  criteriaValue: '',
  shareWithType: 'role',
  shareWithRole: '',
  shareWithUserId: '',
  accessLevel: 'read',
  isActive: true,
  priority: 100,
}

export function SharingRulesPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [showForm, setShowForm] = useState(false)
  const [editingRule, setEditingRule] = useState<SharingRule | null>(null)
  const [formData, setFormData] = useState<Partial<SharingRuleInput>>(emptyRule)
  const [entityFilter, setEntityFilter] = useState<string>('all')
  const [error, setError] = useState<string | null>(null)

  const { data: rulesData, isLoading } = useSharingRules()
  const { data: rolesData } = useRoles()
  const { data: fieldsData } = useEntityFields(formData.entity || 'leads')

  const createRule = useCreateSharingRule()
  const updateRule = useUpdateSharingRule()
  const deleteRule = useDeleteSharingRule()
  const toggleRule = useToggleSharingRule()

  const rules = rulesData?.data || []
  const roles = rolesData?.data || []
  const fields = fieldsData?.data || []

  const ENTITIES = [
    { value: 'leads', label: t('nav.leads') },
    { value: 'accounts', label: t('nav.accounts') },
    { value: 'contacts', label: t('nav.contacts') },
    { value: 'opportunities', label: t('nav.opportunities') },
    { value: 'events', label: t('nav.events') },
    { value: 'notes', label: t('nav.notes') },
  ]

  const OPERATORS = [
    { value: '=', label: t('sharingRules.operators.equals') },
    { value: '!=', label: t('sharingRules.operators.notEquals') },
    { value: '>', label: t('sharingRules.operators.greaterThan') },
    { value: '<', label: t('sharingRules.operators.lessThan') },
    { value: '>=', label: t('sharingRules.operators.greaterOrEqual') },
    { value: '<=', label: t('sharingRules.operators.lessOrEqual') },
    { value: 'contains', label: t('sharingRules.operators.contains') },
    { value: 'starts_with', label: t('sharingRules.operators.startsWith') },
  ]

  const filteredRules = entityFilter === 'all'
    ? rules
    : rules.filter(r => r.entity === entityFilter)

  const canManage = user?.role === 'admin'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    try {
      if (editingRule) {
        await updateRule.mutateAsync({ id: editingRule.id, data: formData as SharingRuleInput })
      } else {
        await createRule.mutateAsync(formData as SharingRuleInput)
      }
      resetForm()
    } catch (err) {
      setError((err as Error).message)
    }
  }

  const resetForm = () => {
    setShowForm(false)
    setEditingRule(null)
    setFormData(emptyRule)
    setError(null)
  }

  const startEdit = (rule: SharingRule) => {
    setEditingRule(rule)
    setFormData({
      name: rule.name,
      description: rule.description || '',
      entity: rule.entity,
      ruleType: rule.ruleType,
      shareFromRole: rule.shareFromRole || '',
      criteriaField: rule.criteriaField || '',
      criteriaOperator: rule.criteriaOperator || '=',
      criteriaValue: rule.criteriaValue || '',
      shareWithType: rule.shareWithType,
      shareWithRole: rule.shareWithRole || '',
      shareWithUserId: rule.shareWithUserId || '',
      accessLevel: rule.accessLevel,
      isActive: rule.isActive,
      priority: parseInt(rule.priority) || 100,
    })
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (confirm(t('common.confirmDelete'))) {
      await deleteRule.mutateAsync(id)
    }
  }

  const getRoleLabel = (role: string) => {
    const found = roles.find(r => r.value === role)
    return found?.label || role
  }

  const getEntityLabel = (entity: string) => {
    const found = ENTITIES.find(e => e.value === entity)
    return found?.label || entity
  }

  if (!canManage) {
    return (
      <>
        <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('sharingRules.title')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('sharingRules.subtitle')}</p>
            </div>
          </div>
        </aside>
        <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
          <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
            <div className="text-center py-12">
              <h2 className="text-lg font-medium text-gray-900">{t('common.accessDenied')}</h2>
              <p className="mt-2 text-sm text-gray-500">{t('users.adminOnly')}</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (isLoading) {
    return (
      <>
        <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </div>
        </aside>
        <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
          <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] flex items-center justify-center">
            <div className="text-gray-500">{t('common.loading')}</div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Secondary column - Filters and form */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('sharingRules.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('sharingRules.subtitle')}</p>
          </div>

          <button
            type="button"
            onClick={() => {
              if (showForm) {
                resetForm()
              } else {
                setShowForm(true)
              }
            }}
            className="w-full flex items-center justify-center gap-x-2 rounded-md px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:opacity-90 bg-brand-500"
          >
            <PlusIcon className="h-5 w-5" />
            {showForm ? t('common.cancel') : t('sharingRules.newRule')}
          </button>

          {/* Create/Edit Form */}
          {showForm && (
            <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
              <h3 className="text-sm font-medium text-gray-900 mb-4">
                {editingRule ? t('sharingRules.editRule') : t('sharingRules.newRule')}
              </h3>
              {error && (
                <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    {t('sharingRules.ruleName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('sharingRules.ruleNamePlaceholder')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700">{t('sharingRules.description')}</label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    {t('sharingRules.entity')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.entity || 'leads'}
                    onChange={(e) => setFormData(prev => ({ ...prev, entity: e.target.value, criteriaField: '' }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  >
                    {ENTITIES.map(entity => (
                      <option key={entity.value} value={entity.value}>{entity.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700">
                    {t('sharingRules.ruleType')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.ruleType || 'owner_based'}
                    onChange={(e) => setFormData(prev => ({ ...prev, ruleType: e.target.value as any }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  >
                    <option value="owner_based">{t('sharingRules.ownerBased')} ({t('sharingRules.ownerBasedDesc')})</option>
                    <option value="criteria_based">{t('sharingRules.criteriaBased')} ({t('sharingRules.criteriaBasedDesc')})</option>
                  </select>
                </div>

                {/* Owner-based fields */}
                {formData.ruleType === 'owner_based' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      {t('sharingRules.shareFromRole')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.shareFromRole || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, shareFromRole: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                    >
                      <option value="">{t('sharingRules.selectRole')}</option>
                      {roles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Criteria-based fields */}
                {formData.ruleType === 'criteria_based' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">
                        {t('sharingRules.field')} <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.criteriaField || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, criteriaField: e.target.value }))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                      >
                        <option value="">{t('sharingRules.selectField')}</option>
                        {fields.map(field => (
                          <option key={field.field} value={field.field}>{field.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">{t('sharingRules.operator')}</label>
                        <select
                          value={formData.criteriaOperator || '='}
                          onChange={(e) => setFormData(prev => ({ ...prev, criteriaOperator: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                        >
                          {OPERATORS.map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">{t('sharingRules.value')}</label>
                        <input
                          type="text"
                          value={formData.criteriaValue || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, criteriaValue: e.target.value }))}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-xs font-medium text-gray-700">
                    {t('sharingRules.shareWith')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.shareWithType || 'role'}
                    onChange={(e) => setFormData(prev => ({ ...prev, shareWithType: e.target.value as any }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  >
                    <option value="role">{t('fields.role')}</option>
                    <option value="user">{t('sharingRules.specificUser')}</option>
                  </select>
                </div>

                {formData.shareWithType === 'role' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700">
                      {t('sharingRules.targetRole')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.shareWithRole || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, shareWithRole: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                    >
                      <option value="">{t('sharingRules.selectRole')}</option>
                      {roles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-700">{t('sharingRules.accessLevel')}</label>
                  <select
                    value={formData.accessLevel || 'read'}
                    onChange={(e) => setFormData(prev => ({ ...prev, accessLevel: e.target.value as any }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
                  >
                    <option value="read">{t('sharingRules.readOnly')}</option>
                    <option value="read_write">{t('sharingRules.readWrite')}</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive ?? true}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">{t('sharingRules.active')}</label>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={createRule.isPending || updateRule.isPending}
                    className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {createRule.isPending || updateRule.isPending ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Entity Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('sharingRules.filterByEntity')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setEntityFilter('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  entityFilter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')} ({rules.length})
              </button>
              {ENTITIES.map(entity => {
                const count = rules.filter(r => r.entity === entity.value).length
                return (
                  <button
                    key={entity.value}
                    onClick={() => setEntityFilter(entity.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      entityFilter === entity.value
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {entity.label} ({count})
                  </button>
                )
              })}
            </div>
          </div>

          {/* Info */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('sharingRules.aboutTitle')}</h3>
            <div className="text-xs text-gray-500 space-y-2">
              <p><strong>{t('sharingRules.ownerBased')}:</strong> {t('sharingRules.aboutOwnerBased')}</p>
              <p><strong>{t('sharingRules.criteriaBased')}:</strong> {t('sharingRules.aboutCriteriaBased')}</p>
              <p>{t('sharingRules.aboutPriority')}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('sharingRules.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('sharingRules.subtitle')}</p>
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t('sharingRules.ruleCol')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('sharingRules.entity')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('sharingRules.typeCol')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('sharingRules.sharesWithCol')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('sharingRules.accessCol')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('sharingRules.status')}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {filteredRules.map((rule) => (
                        <tr key={rule.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="font-medium text-gray-900">{rule.name}</div>
                            {rule.description && (
                              <div className="text-gray-500 text-xs mt-0.5">{rule.description}</div>
                            )}
                            {rule.ruleType === 'owner_based' && rule.shareFromRole && (
                              <div className="text-xs text-gray-400 mt-1">
                                {getRoleLabel(rule.shareFromRole)}
                              </div>
                            )}
                            {rule.ruleType === 'criteria_based' && (
                              <div className="text-xs text-gray-400 mt-1">
                                {rule.criteriaField} {rule.criteriaOperator} "{rule.criteriaValue}"
                              </div>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                              {getEntityLabel(rule.entity)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {rule.ruleType === 'owner_based' ? t('sharingRules.ownerBased') : t('sharingRules.criteriaBased')}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {rule.shareWithType === 'role' && rule.shareWithRole
                              ? getRoleLabel(rule.shareWithRole)
                              : t('sharingRules.specificUser')}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                              rule.accessLevel === 'read_write'
                                ? 'bg-green-50 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {rule.accessLevel === 'read_write' ? t('sharingRules.readWrite') : t('sharingRules.readOnly')}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <button
                              onClick={() => toggleRule.mutate(rule.id)}
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium transition-colors ${
                                rule.isActive
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                            >
                              {rule.isActive ? t('sharingRules.active') : t('sharingRules.inactive')}
                            </button>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => startEdit(rule)}
                                className="text-brand-600 hover:text-brand-900"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(rule.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {filteredRules.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-sm text-gray-500">{t('sharingRules.noRules')}</p>
                      <p className="text-xs text-gray-400 mt-1">{t('sharingRules.noRulesHint')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
